type PaginationProps = {
    currentPage: number;
    totalPages: number;
    onChange: (page: number) => void;
    scroll?: boolean;
};

export const Pagination = ({ currentPage, totalPages, onChange, scroll = true }: PaginationProps) => {
    const previousPages = createListOfPreviousPages(totalPages, currentPage);
    const consecutivePages = createListOfConsecutivePages(totalPages, previousPages[0] || 1, currentPage);

    const handleClick = (event, page) => {
        event.preventDefault();
        onChange(page);

        if (scroll) {
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        }
    };

    const renderPage = (num: number) => (
        // biome-ignore lint/a11y/useValidAnchor: This was set before biome was added
        <a
            key={`pagination${num}`}
            href="#"
            rel="nofollow"
            aria-label={`Side ${num}`}
            aria-current={num === currentPage ? 'page' : undefined}
            data-testid={`page${num}`}
            className={`button button--pill ${num === currentPage ? 'bg-gray-700 hover:bg-gray-700 text-white' : ''}`}
            onClick={(e) => handleClick(e, num)}>
            {num}
        </a>
    );

    return totalPages > 1 ? (
        <nav className="my-16 flex justify-center" aria-labelledby="pagination-heading" data-testid="pagination">
            <h1 className="sr-only" id="pagination-heading">
                Resultatsider
            </h1>
            {previousPages.length > 1 && (
                // biome-ignore lint/a11y/useValidAnchor: This was set before biome was added
                <a
                    href="#"
                    aria-label="Første"
                    data-testid="firstPage"
                    rel="start nofollow"
                    className="button button--pill"
                    onClick={(e) => handleClick(e, 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.53 3.53a.75.75 0 00-1.06-1.06l-5 5a.75.75 0 000 1.06l5 5a.75.75 0 101.06-1.06L10.06 8l4.47-4.47zm-7 0a.75.75 0 00-1.06-1.06l-5 5a.75.75 0 000 1.06l5 5a.75.75 0 001.06-1.06L3.06 8l4.47-4.47z"
                        />
                    </svg>
                </a>
            )}
            {previousPages.length > 0 && (
                // biome-ignore lint/a11y/useValidAnchor: This was set before biome was added
                <a
                    href="#"
                    aria-label="Forrige"
                    data-testid="previousPage"
                    rel="prev nofollow"
                    className="button button--pill"
                    onClick={(e) => handleClick(e, currentPage - 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M11.28 2.22a.75.75 0 010 1.06L6.31 8.25l4.97 4.97a.75.75 0 11-1.06 1.06l-5.5-5.5a.75.75 0 010-1.06l5.5-5.5a.75.75 0 011.06 0z"
                        />
                    </svg>
                </a>
            )}
            <div data-testid="desktopPagination" className="hidden md:inline-flex flex-wrap justify-center">
                {previousPages.map(renderPage)}
                {renderPage(currentPage)}
                {consecutivePages.map(renderPage)}
            </div>
            <div data-testid="mobilePagination" className="inline-block md:hidden p-12 font-bold">
                Side {currentPage}
            </div>
            {consecutivePages.length > 0 && (
                // biome-ignore lint/a11y/useValidAnchor: This was set before biome was added
                <a
                    href="#"
                    aria-label="Neste"
                    data-testid="nextPage"
                    rel="next nofollow"
                    className="button button--pill"
                    onClick={(e) => handleClick(e, currentPage + 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.22 2.22a.75.75 0 011.06 0l5.5 5.5a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 01-1.06-1.06l4.97-4.97-4.97-4.97a.75.75 0 010-1.06z"
                        />
                    </svg>
                </a>
            )}
        </nav>
    ) : null;
};

export function createListOfPreviousPages(totalPages: number, currentPage: number): number[] {
    const minPageNr = Math.min(currentPage - 4, totalPages - 7);
    const firstPage = Math.max(minPageNr, 1);
    const previousPages: number[] = [];
    for (let i = firstPage; i < currentPage; i++) {
        previousPages.push(i);
    }
    return previousPages;
}

export function createListOfConsecutivePages(totalPages: number, firstPage: number, currentPage: number): number[] {
    const lastPage = Math.min(totalPages, firstPage + 7);
    const consecutivePages: number[] = [];
    for (let i = currentPage + 1; i <= lastPage; i++) {
        consecutivePages.push(i);
    }
    return consecutivePages;
}
